import { createFileRoute } from '@tanstack/react-router';
import { assertPublicToolsEnabled } from '@/lib/public-tools-feature';
import { SeoLayout } from '@/components/astro/new-design-25/SEO/seo-layout';
import { seo } from '@/seo/seo';

const heroTitle = 'Research Paper Listener';
const heroDescription =
  'Turn research papers into natural-sounding audio. Follow the text, navigate paper sections, skip citations, and listen at your preferred speed.';

const featureTitle = 'Listen to research papers your way';
const featureSubtitle = 'Academic document audio';
const featureDescription =
  'Upload a research paper and listen to its original text with controls designed for long academic documents.';
const featureBadge = 'Research papers';
const featureVariant = 'default';
const featureCtaText = 'Listen to a Paper';

const featureFeatures = [
  {
    title: 'Navigate Paper Sections',
    description:
      'Jump to sections such as the abstract, methods, results, or discussion.',
  },
  {
    title: 'Skip Citations and References',
    description:
      'Leave citations, references, and footnotes out of the spoken flow.',
  },
  {
    title: 'Read Along While Listening',
    description:
      'Keep the paper visible and follow the text as the audio plays.',
  },
  {
    title: 'Choose Your Listening Speed',
    description:
      'Slow down dense passages or speed up sections you already understand.',
  },
];

const comparisonTableData = {
  title: 'Ways to Read a Research Paper',
  description:
    'Compare Listening with screen-only reading and a basic system reader.',
  columns: [
    { header: 'Listening', isHighlighted: true },
    { header: 'Screen-only PDF', isHighlighted: false },
    { header: 'Basic system reader', isHighlighted: false },
  ],
  rows: [
    {
      feature: 'Reading Mode',
      description: 'How you consume the paper.',
      values: [
        { text: 'Audio with read-along text', icon: 'check' as const },
        { text: 'Visual reading', icon: 'warning' as const },
        { text: 'Audio from selected text', icon: 'warning' as const },
      ],
    },
    {
      feature: 'Paper Navigation',
      description: 'How you move through a structured academic document.',
      values: [
        { text: 'Navigate detected sections', icon: 'check' as const },
        { text: 'Scroll or use PDF bookmarks', icon: 'warning' as const },
        { text: 'Depends on the source app', icon: 'warning' as const },
      ],
    },
    {
      feature: 'Citation Handling',
      description: 'Whether references interrupt the listening flow.',
      values: [
        { text: 'Can skip citations and references', icon: 'check' as const },
        { text: 'Read visually as written', icon: 'warning' as const },
        { text: 'Usually reads selected text', icon: 'warning' as const },
      ],
    },
    {
      feature: 'Playback Control',
      description: 'Options for adjusting the pace.',
      values: [
        { text: 'Adjustable listening speed', icon: 'check' as const },
        { text: 'Not applicable', icon: 'close' as const },
        { text: 'Varies by device', icon: 'warning' as const },
      ],
    },
  ],
  showIcons: true,
  ariaLabel: 'Research Paper Reading Options Comparison Table',
};

const seoSections = [
  {
    title: 'Listen Without Losing the Original Paper',
    content:
      'Listening converts the text of a research paper into audio rather than replacing it with an AI-generated summary. You hear the paper itself and can follow the text while it plays, preserving the author’s wording, qualifications, and context.',
    contentSecondary:
      'Use section navigation to move directly to the abstract, methods, results, or discussion when those sections are detected. You can also skip citations, references, and footnotes so they do not interrupt the main argument.',
  },
  {
    title: 'Fit Academic Reading Into More of Your Day',
    content:
      'Upload papers to your Listening library and continue reading by audio during a walk, commute, or routine task. Adjustable playback speed lets you slow down technical passages and move faster through familiar material.',
    contentSecondary:
      'Listening is a document reader, not a research-analysis or summarization tool. It helps you consume and review papers; interpretation, comparison, and synthesis remain with you.',
  },
];

export const Route = createFileRoute(
  '/_marketing/_seo/research-paper-listener',
)({
  beforeLoad: assertPublicToolsEnabled,
  head: () => ({
    meta: [
      ...seo({
        title: 'Research Paper Listener - Turn Papers Into Audio | Listening',
        description:
          'Turn research papers into natural audio. Follow the text, navigate sections, skip citations, and choose your listening speed.',
      }),
    ],
  }),
  component: () => (
    <SeoLayout
      comparisonTableData={comparisonTableData}
      featureBadge={featureBadge}
      featureCtaText={featureCtaText}
      featureDescription={featureDescription}
      featureFeatures={featureFeatures}
      featureSubtitle={featureSubtitle}
      featureTitle={featureTitle}
      featureVariant={featureVariant}
      pageDescription={heroDescription}
      pageTitle={heroTitle}
      seoSections={seoSections}
    />
  ),
});
